/**
 * GETTERS WARNING: getters aren't reactive, use them with @Watch, $forceUpdate or other Vue 2 tricks
 */

import { AbacoWebModule, RouterReadyEvent, VueRouter, ApplicationReadyEvent, MenuItem } from '@abaco/web-common/src/app';
import globals from "./globals";
import myRoutes from "./routes";
import GabVuexModule from './state';
import { HTTPClient } from '@abaco/web-common/src/HTTP';
import GabApi from './api/GabApi';
import FarmRegistry from '@abaco/farm-registry/src/modules/farm-registry';
import {CodeMap, GabBooking, GabHeader, GabHeaderExtended} from "./models/GabModule";
import { temporaryDayHours } from "./utilities/constants";
import { createMenu } from './menu';
import { Vue } from "vue-property-decorator";
import {formatDateToDateString, formatDateToTimeString} from "./utilities/helpers";

export const MODULE_ID = "gab_module";

export default class GabModule implements AbacoWebModule {
    public static MODULE_ID = MODULE_ID;

    private innerApi!: GabApi;
    private apiCreated = false;

    private router!: VueRouter;
    private http!: HTTPClient;

    readonly id = MODULE_ID;
    readonly routes = myRoutes;
    readonly globalComponents = globals;
    readonly vuexModule = new GabVuexModule();


    menu: MenuItem[] = [];

    dependsOn = {
        [FarmRegistry.MODULE_ID]: () => new FarmRegistry(),
    }

    onRouterReady(evt: RouterReadyEvent) {
        this.router = evt.router;
        this.menu = createMenu(this.router);
    }

    onApplicationReady(evt: ApplicationReadyEvent) {
        this.http = evt.http;
    }

    async beforeEnterAnyRoutes(http: HTTPClient){
        if(this.apiCreated){
            return;
        }
        this.apiCreated = true;
        const GabApi = (await import("./api/GabApi")).default;
        this.innerApi = new GabApi(http);
    }

    public get api(){
        return this.innerApi;
    }

    /**
     * MUTATION
     * @param id
     * @param data
     */
    public addGabBooking(id: number, data: GabBooking){
        // Vue.set(this.vuexModule.bookings, id, data);
        Vue.set(this.vuexModule, 'bookings', {
            ...this.vuexModule.bookings,
            [id]: data
        });
    }

    /**
     * MUTATION
     * @param id
     * @param data
     */
    public editGabBooking(id: number, data: any){
        if(!this.vuexModule.bookings[id])
            return;
        // Vue.set(this.vuexModule.bookings, id, {
        //     ...this.vuexModule.bookings[id],
        //     ...data
        // });
        Vue.set(this.vuexModule, 'bookings', {
            ...this.vuexModule.bookings,
            [id]: {
                ...this.vuexModule.bookings[id],
                ...data,
            }
        })
    }

    /**
     * MUTATION
     * @param id
     * @param data
     */
    public addGabHeader(id: number, data: GabHeader){
        // Vue.set(this.vuexModule.headers, id, data);
        Vue.set(this.vuexModule, 'headers', {
            ...this.vuexModule.headers,
            [id]: data
        });
    }

    /**
     * MUTATION
     * @param id
     * @param data
     */
    public editGabHeader(id: number, data: any){
        if(!this.vuexModule.headers[id])
            return;
        // Vue.set(this.vuexModule.headers, id, {
        //     ...this.vuexModule.headers[id],
        //     ...data
        // });
        Vue.set(this.vuexModule, 'headers', {
            ...this.vuexModule.headers,
            [id]: {
                ...this.vuexModule.headers[id],
                ...data,
            }
        });
    }

    /**
     * MUTATION
     * @param id
     */
    public deleteGabHeader(id: number){
        this.deleteGabHeaderFromBooking(id);
        const temp = { ...this.vuexModule.headers };
        delete temp[id];
        Vue.set(this.vuexModule, 'headers', temp);
        // Vue.delete(this.vuexModule.headers, id);
    }

    /**
     * MUTATION
     * @param id
     */
    public deleteGabHeaderFromBooking(id: number){
        if(!this.vuexModule.headers[id])
            return;
        const { book_id } = this.vuexModule.headers[id];
        if(!book_id || !this.vuexModule.bookings[book_id])
            return;
        // WARNING: NON-nested objects
        const newBooking = {...this.vuexModule.bookings[book_id]};
        if(newBooking.slot1 === id)
            newBooking.slot1 = null;
        else if(newBooking.slot2 === id)
            newBooking.slot2 = null;
        else if(newBooking.slot3 === id)
            newBooking.slot3 = null;
        else if(newBooking.slot4 === id)
            newBooking.slot4 = null;
        // Vue.set(this.vuexModule.bookings, book_id, newBooking);
        const temp = { ...this.vuexModule.bookings };
        temp[book_id] = newBooking;
        Vue.set(this.vuexModule, 'bookings', temp);
    }

    /**
     * GETTER: read warning line 2
     */
    public get dictionaryGabStatus() {
        const dictionary: Record<string, CodeMap> = {};
        const { gabStatus } = this.vuexModule.gabDictionary || {};
        if(!gabStatus)
            return dictionary;
        for(let i = gabStatus.length; i--; )
            dictionary[gabStatus[i].s_code] = gabStatus[i];
        return dictionary;
    }

    /**
     * GETTER: read warning line 2
     */
    public get dictionaryGabActivity() {
        const dictionary: Record<string, CodeMap> = {};
        const { gabActivity } = this.vuexModule.gabDictionary || {};
        if(!gabActivity)
            return dictionary;
        for(let i = gabActivity.length; i--; )
            dictionary[gabActivity[i].s_code] = gabActivity[i];
        return dictionary;
    }

    /**
     * GETTER: read warning line 2
     */
    public get dictionaryGabManureType() {
        const dictionary: Record<string, CodeMap> = {};
        const { gabManurType } = this.vuexModule.gabDictionary || {};
        if(!gabManurType)
            return dictionary;
        for(let i = gabManurType.length; i--; )
            dictionary[gabManurType[i].s_code] = gabManurType[i];
        return dictionary;
    }


    /**
     * GETTER: read warning line 2
     */
    public get dictionaryGabMeasures() {
        const dictionary: Record<string, CodeMap> = {};
        const { gabUom } = this.vuexModule.gabDictionary || {};
        if(!gabUom)
            return dictionary;
        for(let i = gabUom.length; i--; )
            dictionary[gabUom[i].s_code] = gabUom[i];
        return dictionary;
    }

    /**
     * GETTER: read warning line 2
     */
    public get dictionaryGabUom() {
        const dictionary: Record<string, CodeMap> = {};
        const { gabUom } = this.vuexModule.gabDictionary || {};
        if(!gabUom)
            return dictionary;
        for(let i = gabUom.length; i--; )
            dictionary[gabUom[i].s_code] = gabUom[i];
        return dictionary;
    }

    /**
     * GETTER: read warning line 2
     */
    public get dictionaryGabPremise() {
        const dictionary: Record<string, CodeMap> = {};
        const gabPremises = this.vuexModule.premises;
        if(!gabPremises)
            return gabPremises;
        for(const key in gabPremises)
            dictionary[gabPremises[key].pkid] = {s_code: String(gabPremises[key].pkid), p_code: 'GAB_PREMISE', short_descr: gabPremises[key].indirizzo, ext_descr: ''};
        return dictionary;
    }


    /**
     * GETTER: read warning line 2
     */
    public get dictionaryGabDestination() {
        const dictionary: Record<string, CodeMap> = {};
        const gabPremises = this.vuexModule.destinations;
        if(!gabPremises)
            return gabPremises;
        for(const key in gabPremises)
            dictionary[gabPremises[key].pk_cuaa] = {s_code: String(gabPremises[key].pk_cuaa), p_code: 'GAB_DESTINATION', short_descr: gabPremises[key].ragi_soci, ext_descr: ''};
        return dictionary;
    }

    /**
     *
     */
    public get headersExtended(): Record<string, GabHeaderExtended> {
        return Object.fromEntries(
            Object.entries(this.vuexModule.headers)
                .map(([key, header]) => {
                    const h: GabHeaderExtended = {
                        ...header,
                    };
                    if(h.book_id && Object.prototype.hasOwnProperty.call(this.vuexModule.bookings, h.book_id)){
                        // h.time_text = this.vuexModule.bookings[h.book_id].time_text;
                        // h.date_text = this.vuexModule.bookings[h.book_id].date_text;
                        h.book_date_date = this.vuexModule.bookings[h.book_id].book_date_date;
                    }
                    if(h.date){
                        h.time_text = formatDateToTimeString(h.date);
                        h.date_text = formatDateToDateString(h.date);
                    }
                    if(h.activity)
                        h.activity_text = this.dictionaryGabActivity[h.activity]?.short_descr;
                    if(h.manur_type)
                        h.manur_type_text = this.dictionaryGabManureType[h.manur_type]?.short_descr;
                    if(h.status)
                        h.status_text = this.dictionaryGabStatus[h.status]?.short_descr;
                    if(h.uom)
                        h.uom_text = this.dictionaryGabUom[h.uom]?.short_descr;
                    if(h.destination)
                        h.destination_text = this.vuexModule.destinations[h.destination]?.ragi_soci;
                    return [key, h]
                })
        );
    }

    /**
     * Get range of bookings by Date
     *
     * @param start
     * @param end - if missing, default is "next day"
     */
    public getBookingsByDate(start = new Date(), end?: Date) {
        const endTime = end || new Date(new Date(start).setDate(start.getDate() + 1)).getTime();
        return Object.values(this.vuexModule.bookings).filter(({ book_date }) => {
            const book_time = new Date(book_date).getTime();
            return start.getTime() <= book_time && book_time <= endTime;
        });
    }

    /**
     * Get range of headers by Date
     * If {end} is missing, default is "next day"
     *
     * @param start
     * @param end - if missing, default is "next day"
     */
    public getHeadersByDate(start = new Date(), end?: Date) {
        const endTime = end || new Date(new Date(start).setDate(start.getDate() + 1)).getTime();
        return Object.values(this.headersExtended).filter(({ book_id }) => {
            if(!book_id || !Object.prototype.hasOwnProperty.call(this.vuexModule.bookings, book_id))
                return false;
            const time = new Date(this.vuexModule.bookings[book_id].book_date).getTime();
            return start.getTime() <= time && time <= endTime;
        });
    }

    /**
     * Check if there are all day's slots
     * and if all slots are complete
     *
     * Example: A day with 11 hours (11 day's slots) and every hour has 4 slots
     * it means that if we have all slots given by the server we have 4 * 11 = 44 slots.
     *
     * @param date
     * @param items
     */
    public checkDateFull(items: GabBooking[] = [], date = new Date()) {
        if(!temporaryDayHours[date.getDay()] || temporaryDayHours[date.getDay()].length < 2)
            return true;
        return items.length >= (temporaryDayHours[date.getDay()][1] - temporaryDayHours[date.getDay()][0]) &&
            items.every(({ fl_complete }) => fl_complete)
    }
}
